/* Phase 45 — Calculation Trace.
 *
 * Surfaces the engine's per-metric trace block as a hoverable
 * tooltip on Deal Detail. Reads `deal.trace[metricName]` and
 * renders:
 *   - Inputs       (the raw values pulled from the deal)
 *   - Formula      (one-line text describing the math)
 *   - Intermediate (any derived values)
 *   - Final Result
 *
 * Gated behind a parent-level "Show calculation trace" toggle so
 * the chrome only appears when the analyst opts in.
 */
import { useState } from "react";
import { Calculator, ChevronDown, ChevronRight } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

function _fmtVal(v) {
  if (v == null) return "—";
  if (typeof v === "number") {
    if (Math.abs(v) > 1000) return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
    if (Math.abs(v) < 0.01) return v.toFixed(4);
    return v.toFixed(2);
  }
  return String(v);
}

/**
 * Inline calculation-trace tooltip wrapper.
 * Usage:
 *   <CalculationTrace trace={deal.trace} metric="cap_rate">
 *     <span>{fmt.percent(deal.cap_rate)}</span>
 *   </CalculationTrace>
 */
export function CalculationTrace({ trace, metric, children, enabled = true }) {
  if (!enabled || !trace || !trace[metric]) return children;
  const t = trace[metric];
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            data-testid={`trace-${metric}-trigger`}
            className="border-b border-dotted border-slate-300 cursor-help"
          >
            {children}
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="max-w-[360px] bg-white text-left text-xs"
          data-testid={`trace-${metric}-tooltip`}
        >
          <div className="flex items-center gap-1 border-b border-slate-100 pb-1 text-[10px] font-semibold uppercase tracking-wider text-indigo-600">
            <Calculator className="h-3 w-3" /> {metric}
          </div>
          <div className="mt-2 space-y-1.5">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Inputs</div>
              <dl className="mt-0.5">
                {Object.entries(t.inputs || {}).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-3">
                    <dt className="text-slate-600">{k}</dt>
                    <dd className="tabular-nums text-slate-900">{_fmtVal(v)}</dd>
                  </div>
                ))}
              </dl>
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Formula</div>
              <div className="mt-0.5 font-mono text-[11px] leading-snug text-slate-700">{t.formula}</div>
            </div>
            {t.intermediates && Object.keys(t.intermediates).length ? (
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Intermediates</div>
                <dl className="mt-0.5">
                  {Object.entries(t.intermediates).map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-3">
                      <dt className="text-slate-600">{k}</dt>
                      <dd className="tabular-nums text-slate-900">{_fmtVal(v)}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ) : null}
            <div className="border-t border-slate-100 pt-1">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700">Result</div>
              <div className="mt-0.5 tabular-nums font-semibold text-slate-900">{_fmtVal(t.result)}</div>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}


/** Header toggle — flip this and every <CalculationTrace> tooltip is gated. */
export function CalculationTraceToggle({ enabled, onToggle }) {
  return (
    <button
      type="button"
      data-testid="calc-trace-toggle"
      onClick={() => onToggle(!enabled)}
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider transition-colors ${
        enabled
          ? "bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200 hover:bg-indigo-100"
          : "bg-slate-50 text-slate-600 ring-1 ring-slate-200 hover:bg-slate-100"
      }`}
      title={enabled ? "Hide calculation trace tooltips" : "Show calculation trace tooltips on every metric"}
    >
      {enabled ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
      <Calculator className="h-3 w-3" />
      {enabled ? "Hide trace" : "Show calculation trace"}
    </button>
  );
}


/** Convenience hook — manages enabled state locally; lifts to the
 *  parent so children can wire onto it. */
export function useCalculationTrace(defaultEnabled = false) {
  const [enabled, setEnabled] = useState(defaultEnabled);
  return { enabled, setEnabled };
}

export default CalculationTrace;
